import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {ExpandableSearchBar} from '@Components/expandable-search-bar';
import {AddAccount} from '@Components/social-media/components/add-account';
import {Checkbox} from '@Components/checkbox-legacy';
import {useAccountIdsToPublishTo, useEmphasizedPlatform, useLoadSocialAccountsForWizard, useSocialMediaWizardAccounts} from '@Components/social-media/social-media.hooks';
import {SocialAccountListItem} from '@Components/social-media-wizard/components/social-account-list-item';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {
  addConnectAccountBroadcastChannel,
  getAccountConnectionResponseFromBroadcast,
  handleAccountConnectionResponse,
  isAccountInSearch,
  isEligibleForSocialAccountsUpsellDialog,
  removeConnectAccountBroadcastChannel,
  sortPlanLockedAccountsLast,
} from '@Libraries/social-media-account.library';
import {
  addAccountIdForPublishing,
  disableNextButton,
  enableNextButton,
  removeAccountIdFromPublishing,
  updateAccountIdsToPublishTo,
  updateEmphasizedPlatform,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {SelectedAccountsHeader} from './components/selected-accounts-header/selected-accounts-header';
import {getAccountForId, getAccountPublishabilityInfo} from '@Libraries/social-media-wizard-accounts-library';
import type {AccountPublishabilityInfo} from '@Libraries/social-media-wizard-accounts-library';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {
  useAccountLevelErrorMessage,
  useAreAccountsLoading,
  useDesignsToPublish,
  useIsGoogleBusinessProfileEnabled,
  useNeedsResizeMap,
  useNumParentAccountsConnected,
  useWizardDesignsGraphicsMetaMap,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {compareByReconnectFirst} from '@Libraries/social-media-error.library';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {MoreAccountsUpsell} from '@Components/more-accounts-upsell';
import {ErrorBox} from '@Components/error-box';
import styles from './social-accounts-list.module.scss';
import {useAppDispatch} from '@/hooks';
import {getPostableSocialMediaAccountsForUser} from '@Components/social-media/social-media-thunk';
import {useAccountPreselection} from './social-accounts-list.hooks';

export function SocialAccountsList(): ReactElement<any> {
  const accounts = useSocialMediaWizardAccounts();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const accountLevelErrorMessage = useAccountLevelErrorMessage();
  const emphasizedPlatform = useEmphasizedPlatform();
  const [areAllSelected, setAreAllSelected] = useState(false);
  const [isListLoaded, setIsListLoaded] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const dispatch = useAppDispatch();
  const numSelectedAccounts = accountIdsToPublishTo.length;
  const areAccountsLoading = useAreAccountsLoading();
  const numParentAccountsConnected = useNumParentAccountsConnected();
  const accountSearchContainerId = 'account-search-container-id';
  const posters = useDesignsToPublish();
  const designsGraphicsMetaMap = useWizardDesignsGraphicsMetaMap();
  const accountNeedsResizeMapForDesigns = useNeedsResizeMap();
  const isGoogleBusinessProfileEnabled = useIsGoogleBusinessProfileEnabled();

  const getPublishabilityForAccount = (account: SocialAccount): AccountPublishabilityInfo => {
    return getAccountPublishabilityInfo({
      account,
      posters,
      accountNeedsResizeMap: accountNeedsResizeMapForDesigns,
      designsGraphicsMetaMap,
      infoIconClass: styles.infoIcon,
      isGraphicsMetaLoading: isGraphicsMetaLoadingState(),
    });
  };

  const isGraphicsMetaLoadingState = (): boolean => {
    if (Object.keys(designsGraphicsMetaMap).length === 0) {
      return true;
    }
    return Object.values(designsGraphicsMetaMap)
      .flatMap((pageMeta) => Object.values(pageMeta))
      .some((graphicMeta) => graphicMeta.graphicURL === '');
  };

  const getAvailableSocialAccounts = (): SocialAccount[] => {
    const disabledPlatforms: SocialPlatform[] = [];

    if (!isGoogleBusinessProfileEnabled) {
      disabledPlatforms.push(SocialPlatform.GOOGLE_BUSINESS_PROFILE);
    }

    const filteredAccounts = accounts.filter((account) => {
      return !disabledPlatforms.includes(account.type);
    });

    return sortPlanLockedAccountsLast(filteredAccounts);
  };

  const availableAccounts = getAvailableSocialAccounts();

  const getNumAccountsToPublishTo = (): number => {
    let i = 0;

    accountIdsToPublishTo.forEach((accountId) => {
      if (isAccountPublishable(accountId)) {
        i += 1;
      }
    });

    return i;
  };

  const toggleButtonByAccounts = (): void => {
    if (numSelectedAccounts > 0) {
      dispatch(enableNextButton());
    } else {
      dispatch(disableNextButton());
    }
  };

  useEffect(() => {
    toggleButtonByAccounts();
  }, []);

  useEffect(() => {
    toggleButtonByAccounts();

    return (): void => {
      dispatch(enableNextButton());
    };
  }, [numSelectedAccounts]);

  useDidUpdateEffect(() => {
    if (accounts.length === 0) {
      dispatch(disableNextButton());
    }
  }, [accounts, areAllSelected, accountSearchTerm]);

  useLoadSocialAccountsForWizard();

  const doReloadAccounts = (broadcastEvent: MessageEvent): void => {
    const connectionResponse = getAccountConnectionResponseFromBroadcast(broadcastEvent);

    handleAccountConnectionResponse(connectionResponse, () => {
      void dispatch(getPostableSocialMediaAccountsForUser());
    });
  };

  useEffect(() => {
    const accountCreatedBroadcastChannel = addConnectAccountBroadcastChannel(doReloadAccounts);

    return (): void => {
      removeConnectAccountBroadcastChannel(accountCreatedBroadcastChannel);
    };
  }, []);

  const isAccountSelected = (account: SocialAccount): boolean => {
    return (areAllSelected || accountIdsToPublishTo.includes(account.id)) && isAccountPublishable(account.id);
  };

  const isAccountPublishable = (accountId: number): boolean => {
    const currentAccount = getAccountForId(accounts, accountId);

    if (!currentAccount) {
      return false;
    }

    return getPublishabilityForAccount(currentAccount).isPublishable;
  };

  const getAccountsOrderedForDisplay = (): SocialAccount[] => {
    return [...availableAccounts].sort(compareByReconnectFirst);
  };

  const getListItems = (): ReactElement<any>[] => {
    const accountCheckItems: ReactElement<any>[] = [];

    getAccountsOrderedForDisplay().forEach((account) => {
      const {isPublishable, tooltip} = getPublishabilityForAccount(account);

      accountCheckItems.push(
        <li key={`list-${account.id}`} className={`spacing-m-b-1 _unpadded ${!isAccountInSearch(accountSearchTerm, account.name) ? '_hidden' : ''}`}>
          <SocialAccountListItem
            key={`social-account-item-${account.id}`}
            account={account}
            tooltip={tooltip}
            isDisabled={!isPublishable}
            isSelected={isAccountSelected(account)}
            onCheck={onAccountChecked}
            onUncheck={onAccountUnchecked}
          />
        </li>
      );
    });

    return accountCheckItems;
  };

  const moveToConnectAccountsStep = (): void => {
    dispatch(updateWizardStep(SocialMediaWizardStep.CONNECT_ACCOUNTS));
  };

  const onAccountChecked = (idAccount: number): void => {
    onAccountSelected(idAccount);
    onAccountSelectionUpdated();
  };

  const onAccountUnchecked = (idAccount: number): void => {
    onAccountUnselected(idAccount);
    onAccountSelectionUpdated();
  };

  const onAccountSelectionUpdated = (): void => {
    dispatch(updateEmphasizedPlatform(undefined));
  };

  const onAccountSelected = (idAccount: number): void => {
    dispatch(addAccountIdForPublishing(idAccount));
  };

  useAccountPreselection({
    accounts,
    availableAccounts,
    accountIdsToPublishTo,
    accountNeedsResizeMapForDesigns,
    emphasizedPlatform,
    isListLoaded,
    areAccountsLoading,
    setIsListLoaded,
    isAccountPublishable,
    onAccountSelected,
  });

  const onAccountUnselected = (idAccount: number): void => {
    dispatch(removeAccountIdFromPublishing(idAccount));
  };

  const onSelectAll = (): void => {
    const newIsSelected = !areAllSelected;

    setAreAllSelected(newIsSelected);
    if (newIsSelected) {
      dispatch(updateAccountIdsToPublishTo(getAllSelectableAccountIds()));
    } else {
      dispatch(updateAccountIdsToPublishTo([]));
    }

    onAccountSelectionUpdated();
  };

  const onDeselectAll = (): void => {
    setAreAllSelected(false);

    dispatch(updateAccountIdsToPublishTo([]));
    onAccountSelectionUpdated();
  };

  const getAllSelectableAccountIds = (): number[] => {
    const accountIds: number[] = [];

    availableAccounts.forEach((account) => {
      if (isAccountPublishable(account.id)) {
        accountIds.push(account.id);
      }
    });

    return accountIds;
  };

  const getSelectAllOption = (): ReactElement<any> => {
    return (
      <li className="spacing-m-b-1 _unpadded">
        <ClickableDiv className={`_cursor-pointer flex-h-row spacing-p-2 radius-4 ${styles.listItem} ${areAllSelected ? styles.selected : ''} `} onClick={onSelectAll}>
          <Checkbox className="spacing-m-r-5" isChecked={areAllSelected} />
          <Text val={window.i18next.t('pmwjs_select_all')} size={TextSize.XSMALL} />
        </ClickableDiv>
      </li>
    );
  };

  const onSearchInputChange = (searchTerm: string): void => {
    setAccountSearchTerm(searchTerm);
  };

  const getLoadingAccounts = (): ReactElement<any>[] => {
    const loadingAccounts = [];
    const nLoadingAccounts = 4;

    for (let i = 0; i < nLoadingAccounts; i++) {
      loadingAccounts.push(
        <div className={`flex-row-align-center ${styles.loadingAccountContainer}`} key={`skeleton-loader-${i}`}>
          <SkeletonBrick height={16} width={16} className={`spacing-m-r-4 ${styles.loadingAccountIndex}`} />
          <SkeletonBrick height={32} width={32} className={`spacing-m-r-4 ${styles.loadingAccountPicture}`} />
          <SkeletonBrick height={16} className={`spacing-m-r-4 ${styles.loadingAccountDetails}`} />
        </div>
      );
    }

    return loadingAccounts;
  };

  const onSearchFocus = (): void => {
    document.getElementById(accountSearchContainerId)?.scrollIntoView({behavior: 'smooth', block: 'start'});
  };

  const getLoadingState = (): ReactElement<any> => {
    return (
      <>
        <SkeletonBrick height={40} className={`${styles.skeletonHeader} spacing-m-b-3`} />
        {getLoadingAccounts()}
      </>
    );
  };

  const getAddAccountButton = (): ReactElement<any> => {
    if (isEligibleForSocialAccountsUpsellDialog(numParentAccountsConnected)) {
      return (
        <div className="spacing-m-t-2">
          <MoreAccountsUpsell />
        </div>
      );
    }

    return (
      <div className={`spacing-p-t-2 spacing-p-b-2 ${styles.addAccountWrapper}`}>
        <AddAccount onCustomAdd={moveToConnectAccountsStep} />
      </div>
    );
  };

  if (areAccountsLoading) {
    return getLoadingState();
  }

  const getSearchBarElement = (): ReactElement<any> => {
    return (
      <div id={accountSearchContainerId}>
        <ExpandableSearchBar
          onFocus={onSearchFocus}
          onInputChange={onSearchInputChange}
          classes="spacing-m-b-3"
          placeholder={window.i18next.t('pmwjs_search_accounts_ellipsis')}
          isExpanded
        />
      </div>
    );
  };

  const getErrorMessageBox = (): ReactElement<any> | null => {
    if (accountLevelErrorMessage.title === '' && accountLevelErrorMessage.description === '') {
      return null;
    }

    return (
      <div className="spacing-m-b-4">
        <ErrorBox title={accountLevelErrorMessage.title} description={accountLevelErrorMessage.description} />
      </div>
    );
  };

  return (
    <div>
      {getErrorMessageBox()}
      <SelectedAccountsHeader
        availableAccounts={availableAccounts}
        accountIdsToPublishTo={accountIdsToPublishTo}
        numAccountsToPublishTo={getNumAccountsToPublishTo()}
        onAccountUnselected={onAccountUnselected}
        onDeselectAll={onDeselectAll}
        isAccountPublishable={isAccountPublishable}
      />
      {getSearchBarElement()}
      {getAddAccountButton()}
      <ul id="select-accounts-onboarding-container" className="spacing-m-t-3">
        {getSelectAllOption()}
        {getListItems()}
      </ul>
    </div>
  );
}
